import { Box, FormControlLabel, Switch, TextField, Typography } from '@mui/material';
import styles from '../styles';
import moment from 'moment/moment';
import { ITemplate } from '../../../../resource';
import { ThemeSelector } from '../../../../components/ThemeSelector';
import { ImageEditor } from '../../../../components/ImageEditor';
import React, { useContext, useState, useEffect } from 'react';
import { UserSelector } from '../../../../components/UserSelector';
import { AppContext } from '../../../../context';
import { ITheme } from '../../../../resource/interfaces';
import apis from '../../../../apis';

type Props = {
  template?: ITemplate;
  onChange: (value: Partial<ITemplate>) => void;
};

export const BasicInfo = ({ template, onChange }: Props) => {
  const { isAdmin } = useContext(AppContext);
  const [themes, setThemes] = useState<ITheme[]>([]);

  useEffect(() => {
    loadThemes();
  }, []);

  const loadThemes = async () => {
    try {
      const result = await apis.theme.getThemes();
      if (result.success) {
        setThemes(result.themes);
      }
    } catch (error) {
      console.error('Failed to load themes:', error);
    }
  };

  return (
    <>
      <Box sx={{ mb: 8 }}>
        <Typography variant="h5" fontWeight={500}>
          Basic Information
        </Typography>
      </Box>
      <Box sx={{ width: '100%', display: 'flex', gap: 46, flexWrap: 'wrap' }}>
        <Box sx={styles.template}>
          <ImageEditor
            value={template?.thumbnail_url}
            onChange={(value) => onChange({ thumbnail_url: value })}
          />
        </Box>
        <Box flex={1} minWidth={280}>
          <TextField
            placeholder="Title"
            sx={styles.titleInput}
            value={template?.name || ''}
            onChange={(e) => onChange({ name: e.target.value })}
          />
          <TextField
            multiline
            placeholder="Description"
            sx={styles.descInput}
            value={template?.description || ''}
            onChange={(e) => onChange({ description: e.target.value })}
          />

          <Typography variant="body2" sx={{ opacity: 0.5, mb: 16 }}>
            {template?.createdAt
              ? moment(template.createdAt).format('DD MMMM YYYY / hh:mm:ss')
              : '—'}
          </Typography>

          <ThemeSelector
            selectedThemeId={
              typeof template?.theme === 'string' ? template.theme : template?.theme?._id
            }
            onChange={(themeId) => {
              const selectedTheme = themes.find((t) => t._id === themeId);
              onChange({
                theme: themeId,
                color: selectedTheme?.mainColor || template?.color || '#1976d2',
                colorSets: selectedTheme?.colorSet?.gradientColors,
              });
            }}
          />

          {/* Still used by PDF generation (PVM/GPS, SQUAD, V1) — keep as simple fields, not a fake designer. */}
          <Box sx={{ mt: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <TextField
              label="Fund title (fond)"
              size="small"
              value={template?.fond || ''}
              onChange={(e) => onChange({ fond: e.target.value })}
              helperText="Used as a fallback title in some PDF headers."
            />
            <Box sx={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
              <Box>
                <Typography variant="body2" sx={{ mb: 4, opacity: 0.7 }}>
                  Icon / logo (icon_url)
                </Typography>
                <Box sx={{ width: 120, height: 120 }}>
                  <ImageEditor
                    value={template?.icon_url}
                    onChange={(value) => onChange({ icon_url: value })}
                  />
                </Box>
              </Box>
              <Box>
                <Typography variant="body2" sx={{ mb: 4, opacity: 0.7 }}>
                  Intro image (manImageUrl)
                </Typography>
                <Box sx={{ width: 120, height: 120 }}>
                  <ImageEditor
                    value={template?.manImageUrl}
                    onChange={(value) => onChange({ manImageUrl: value })}
                  />
                </Box>
              </Box>
            </Box>
          </Box>

          {isAdmin && (
            <Box sx={{ mt: 16 }}>
              <UserSelector
                multiple
                value={template?.userIds || []}
                onChange={(value) => onChange({ userIds: value || [] })}
              />
              <Box sx={{ mt: 12, display: 'flex', flexDirection: 'column', gap: 4 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!template?.isPublished}
                      onChange={(e) => onChange({ isPublished: e.target.checked })}
                    />
                  }
                  label="Published (visible on Templates list)"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!template?.isDeleted}
                      onChange={(e) => onChange({ isDeleted: e.target.checked })}
                    />
                  }
                  label="Archived"
                />
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
};
